import { useCallback, useEffect, useMemo, useState } from "react";
import {
  applySemanticImportToSpec,
  buildImportReview,
  buildSampleSemanticImportWorkbookBuffer,
  canApplySemanticImport,
  hasBlockingIncludedError,
  parseSemanticImportWorkbook,
  SAMPLE_SEMANTIC_IMPORT_FILENAME,
  summarizeImportApply,
  type ParsedImportRow,
  type SemanticImportConflictMode,
  type SemanticImportSeverity,
  type SemanticSpecImportShape
} from "../lib/semanticExcelImport.js";

type WizardStep = "file" | "review" | "confirm";

function statusIcon(severity: SemanticImportSeverity): string {
  switch (severity) {
    case "ok":
      return "✓";
    case "warning":
      return "⚠";
    default:
      return "✕";
  }
}

function ImportStatusInline({ severity, messages }: { severity: SemanticImportSeverity; messages: string[] }) {
  const text =
    severity === "ok"
      ? messages.length > 0
        ? messages.join("; ")
        : "OK"
      : messages.join("; ");
  const aria = `${severity}: ${text}`;
  return (
    <span
      className={`semanticImportStatus semanticImportStatus_${severity}`}
      aria-label={aria}
      title={text}
      style={{ display: "inline-flex", alignItems: "flex-start", gap: 6, flexWrap: "wrap" }}
    >
      <span aria-hidden="true">{statusIcon(severity)}</span>
      <span className="semanticImportStatusMsg">{text}</span>
    </span>
  );
}

export function SemanticImportWizard({
  open,
  onClose,
  spec,
  activeSaId,
  onApply
}: {
  open: boolean;
  onClose: () => void;
  spec: SemanticSpecImportShape;
  activeSaId: string;
  onApply: (next: SemanticSpecImportShape) => void;
}) {
  const [step, setStep] = useState<WizardStep>("file");
  const [fileName, setFileName] = useState("");
  const [parseError, setParseError] = useState<string | null>(null);
  const [parsedRows, setParsedRows] = useState<ParsedImportRow[] | null>(null);
  const [includedRowIds, setIncludedRowIds] = useState<Set<string>>(() => new Set());
  const [objectIncluded, setObjectIncluded] = useState<Record<string, boolean>>({});
  const [resolutions, setResolutions] = useState<Record<string, SemanticImportConflictMode>>({});

  const reset = useCallback(() => {
    setStep("file");
    setFileName("");
    setParseError(null);
    setParsedRows(null);
    setIncludedRowIds(new Set());
    setObjectIncluded({});
    setResolutions({});
  }, []);

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  const groups = useMemo(() => {
    if (!parsedRows) return [];
    return buildImportReview(spec, parsedRows, resolutions);
  }, [spec, parsedRows, resolutions]);

  const summaryCounts = useMemo(() => {
    let errors = 0;
    let warnings = 0;
    for (const g of groups) {
      for (const rr of g.rows) {
        if (rr.severity === "error") errors++;
        else if (rr.severity === "warning") warnings++;
      }
    }
    return { errors, warnings };
  }, [groups]);

  const applySummary = useMemo(
    () => summarizeImportApply(groups, objectIncluded, includedRowIds, resolutions),
    [groups, objectIncluded, includedRowIds, resolutions]
  );

  const blocked = hasBlockingIncludedError(groups, objectIncluded, includedRowIds);
  const canApply = canApplySemanticImport(groups, objectIncluded, includedRowIds, resolutions);

  const downloadSampleImportSheet = useCallback(() => {
    const buf = buildSampleSemanticImportWorkbookBuffer();
    const blob = new Blob([buf], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = SAMPLE_SEMANTIC_IMPORT_FILENAME;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, []);

  const onPickFile = useCallback(async (file: File | null) => {
    setParseError(null);
    setParsedRows(null);
    if (!file) return;
    setFileName(file.name);
    try {
      const buffer = await file.arrayBuffer();
      const result = parseSemanticImportWorkbook(buffer);
      if (!result.ok) {
        setParseError(result.error);
        return;
      }
      const rows = result.rows;
      setParsedRows(rows);
      const nextRes: Record<string, SemanticImportConflictMode> = {};
      const initGroups = buildImportReview(spec, rows, {});
      for (const g of initGroups) {
        if (g.hasConflict) nextRes[g.objectKey] = "merge";
      }
      setResolutions(nextRes);
      setIncludedRowIds(new Set(rows.map((r) => r.id)));
      setObjectIncluded({});
      setStep("review");
    } catch (e) {
      setParseError(e instanceof Error ? e.message : String(e));
    }
  }, [spec]);

  const setObjectInclude = useCallback(
    (objectKey: string, include: boolean, rowIdsInGroup: string[]) => {
      if (!include) {
        setObjectIncluded((p) => ({ ...p, [objectKey]: false }));
      } else {
        setObjectIncluded((p) => {
          const n = { ...p };
          delete n[objectKey];
          return n;
        });
        setIncludedRowIds((prev) => {
          const n = new Set(prev);
          for (const id of rowIdsInGroup) n.add(id);
          return n;
        });
      }
    },
    []
  );

  const setRowInclude = useCallback((rowId: string, include: boolean) => {
    setIncludedRowIds((prev) => {
      const n = new Set(prev);
      if (include) n.add(rowId);
      else n.delete(rowId);
      return n;
    });
  }, []);

  const setResolution = useCallback((objectKey: string, mode: SemanticImportConflictMode) => {
    setResolutions((p) => ({ ...p, [objectKey]: mode }));
  }, []);

  const handleApply = useCallback(() => {
    if (!parsedRows || !canApply) return;
    const next = applySemanticImportToSpec(spec, parsedRows, {
      includedRowIds,
      objectIncluded,
      resolutions,
      activeSaId
    });
    onApply(next);
    onClose();
  }, [
    parsedRows,
    canApply,
    spec,
    includedRowIds,
    objectIncluded,
    resolutions,
    activeSaId,
    onApply,
    onClose
  ]);

  if (!open) return null;

  return (
    <div className="modalOverlay" role="presentation" onClick={onClose}>
      <div
        className="modal modalScrollable semanticImportWizardModal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="semantic-import-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modalScrollHeader">
          <div className="reportModalTitle" id="semantic-import-title">
            Import from Excel
          </div>
          <p className="muted" style={{ fontSize: 12, marginTop: 6, marginBottom: 0 }}>
            Step {step === "file" ? "1" : step === "review" ? "2" : "3"} of 3 —{" "}
            {step === "file" ? "Choose file" : step === "review" ? "Review & resolve" : "Confirm"}
          </p>
        </div>

        <div className="modalScrollBody">
          {step === "file" ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <p style={{ fontSize: 13, margin: 0 }}>
                Select an Excel file (.xlsx or .xls). The first sheet must include columns for object name, field/column
                name, and data type. Parsing runs in your browser; nothing is uploaded.
              </p>
              <p style={{ fontSize: 12, margin: 0 }} className="muted">
                The sample workbook lists every column: <strong>object_name</strong>, <strong>column_name</strong>,{" "}
                <strong>data_type</strong>, <strong>pk</strong>, <strong>security_class</strong>,{" "}
                <strong>exposure_mode</strong>, <strong>table_description</strong>, <strong>column_description</strong>{" "}
                (optional text for the object and each field; the first non-blank <strong>table_description</strong> per
                object in sheet order becomes the object description). <strong>security_class</strong> is a column&apos;s
                access: <strong>allow</strong> or <strong>restrict</strong>; blank means allow.
              </p>
              <div>
                <button type="button" className="btn btnSm" onClick={downloadSampleImportSheet}>
                  Download sample import sheet
                </button>
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label className="label" htmlFor="semantic-import-file">
                  File
                </label>
                <input
                  id="semantic-import-file"
                  type="file"
                  accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                  onChange={(e) => void onPickFile(e.target.files?.[0] ?? null)}
                />
              </div>
              {fileName ? (
                <span className="muted" style={{ fontSize: 12 }}>
                  Selected: {fileName}
                </span>
              ) : null}
              {parseError ? (
                <div className="semanticImportParseError" role="alert">
                  {parseError}
                </div>
              ) : null}
            </div>
          ) : null}

          {step === "review" && parsedRows ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ fontSize: 12 }} className="muted">
                Compared to the full model ({spec.objects.length} object{spec.objects.length === 1 ? "" : "s"}).{" "}
                <strong>{summaryCounts.errors}</strong> row{summaryCounts.errors === 1 ? "" : "s"} with errors,{" "}
                <strong>{summaryCounts.warnings}</strong> with warnings. Uncheck rows or whole objects to exclude them
                from import. Deselect all error rows (or fix the file) before continuing.
              </div>
              {blocked ? (
                <div className="semanticImportBlocked" role="status">
                  Included rows still contain errors. Uncheck those rows or choose another file.
                </div>
              ) : null}
              <div className="semanticImportObjectList">
                {groups.map((g) => {
                  const rowIds = g.rows.map((r) => r.row.id);
                  const objOn = objectIncluded[g.objectKey] !== false;
                  return (
                    <div key={g.objectKey} className="semanticImportObjectBlock">
                      <div className="semanticImportObjectHeader">
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                          <input
                            type="checkbox"
                            checked={objOn}
                            aria-label={`Include object ${g.displayName}`}
                            onChange={(e) => setObjectInclude(g.objectKey, e.target.checked, rowIds)}
                          />
                          <strong>{g.displayName || "(unnamed object)"}</strong>
                          {g.hasConflict ? (
                            <select
                              className="input"
                              style={{ maxWidth: 200, fontSize: 12 }}
                              value={resolutions[g.objectKey] ?? "merge"}
                              aria-label={`Import action for ${g.displayName}`}
                              onChange={(e) => setResolution(g.objectKey, e.target.value as SemanticImportConflictMode)}
                            >
                              <option value="merge">Merge fields</option>
                              <option value="replace">Replace all fields</option>
                              <option value="skip">Skip object</option>
                            </select>
                          ) : null}
                          <ImportStatusInline severity={g.groupSeverity} messages={g.groupMessages} />
                        </span>
                      </div>
                      <table className="semanticImportTable">
                        <thead>
                          <tr>
                            <th>In</th>
                            <th>Row</th>
                            <th>Field</th>
                            <th>Type</th>
                            <th>PK</th>
                            <th>Object desc</th>
                            <th>Field desc</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.rows.map((rr) => {
                            const rowOn = objOn && includedRowIds.has(rr.row.id);
                            return (
                              <tr key={rr.row.id}>
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={rowOn}
                                    disabled={!objOn}
                                    aria-label={`Include row ${rr.row.sheetRowNumber} ${rr.row.columnName}`}
                                    onChange={(e) => setRowInclude(rr.row.id, e.target.checked)}
                                  />
                                </td>
                                <td>{rr.row.sheetRowNumber}</td>
                                <td>{rr.row.columnName || "—"}</td>
                                <td>{rr.row.dataType}</td>
                                <td>{rr.row.primaryKey ? "Yes" : ""}</td>
                                <td title={rr.row.tableDescription || undefined}>
                                  {rr.row.tableDescription ? (
                                    <span className="muted" style={{ fontSize: 11 }}>
                                      {rr.row.tableDescription.length > 48
                                        ? `${rr.row.tableDescription.slice(0, 48)}…`
                                        : rr.row.tableDescription}
                                    </span>
                                  ) : (
                                    ""
                                  )}
                                </td>
                                <td title={rr.row.columnDescription || undefined}>
                                  {rr.row.columnDescription ? (
                                    <span className="muted" style={{ fontSize: 11 }}>
                                      {rr.row.columnDescription.length > 48
                                        ? `${rr.row.columnDescription.slice(0, 48)}…`
                                        : rr.row.columnDescription}
                                    </span>
                                  ) : (
                                    ""
                                  )}
                                </td>
                                <td>
                                  <ImportStatusInline severity={rr.severity} messages={rr.messages} />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          {step === "confirm" && parsedRows ? (
            <div style={{ fontSize: 13, display: "flex", flexDirection: "column", gap: 10 }}>
              <p style={{ margin: 0 }}>You are about to update the semantic model from this import:</p>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>New objects: {applySummary.newObjects}</li>
                <li>Merged into existing: {applySummary.merged}</li>
                <li>Replaced fields on existing: {applySummary.replaced}</li>
                <li>Objects skipped: {applySummary.skipped}</li>
                <li>Field rows applied: {applySummary.fieldRowsApplied}</li>
              </ul>
              {blocked ? (
                <div className="semanticImportBlocked" role="alert">
                  Cannot apply: included rows still have errors.
                </div>
              ) : null}
              {!canApply && !blocked ? (
                <div className="muted" role="status">
                  Nothing to apply — include at least one valid field row.
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="modalScrollFooter">
          <div className="actions" style={{ justifyContent: "space-between", width: "100%" }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {step !== "file" ? (
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    if (step === "confirm") setStep("review");
                    else if (step === "review") {
                      setStep("file");
                      setParsedRows(null);
                      setParseError(null);
                      setFileName("");
                    }
                  }}
                >
                  Back
                </button>
              ) : null}
              <button type="button" className="btn" onClick={onClose}>
                Cancel
              </button>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {step === "review" ? (
                <button type="button" className="btn btnPrimary" disabled={blocked} onClick={() => setStep("confirm")}>
                  Next
                </button>
              ) : null}
              {step === "confirm" ? (
                <button type="button" className="btn btnPrimary" disabled={!canApply} onClick={handleApply}>
                  Apply import
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
